// src/components/quizzes/BonusDiamonds.js
//
// The "Diamond Tap" bonus round.
//
// ===========================================================================
// SAME CONTRACT AS BEFORE
// ===========================================================================
// BonusRouter renders this as:
//     <BonusComponent onBonusComplete questions placeholderImage />
// and UniversalQuizGame takes the number it hands back as the bonus score.
// That interface has not changed, so this is a drop-in replacement.
//
// ===========================================================================
// WHAT CHANGED
// ===========================================================================
// The old round was one blue diamond worth 5 points, falling at a random
// speed, for 25 seconds. Every tap was worth the same, so there was no reason
// to aim at anything in particular — optimal play was to flail at the screen.
// Kids figured that out fast and it stopped being a game.
//
//   1. THREE GEM TIERS. Blue 5, purple 10, gold 25. The valuable ones fall
//      faster and appear less often, so there is now a decision in every
//      second of play: chase the gold or bank the blues.
//
//      All three are the SAME image file with a CSS hue-rotate on it. No new
//      art needed in Storage, and no new asset to 404 in production.
//
//   2. BOMBS. Roughly one in ten drops is a bomb worth -5 and it breaks your
//      combo. This is what makes the flailing strategy stop working. Score is
//      floored at zero — a child can never end a bonus round in the negative,
//      which matters more than the game theory does.
//
//   3. COMBO MULTIPLIER. Five catches in a row doubles every gem; ten trebles
//      it. A bomb resets it. The meter is on screen the whole time.
//
//   4. FLOATING SCORE POPS. Every catch throws its own value up the screen.
//      Previously a tap produced confetti and a silently-incremented counter,
//      so there was no feedback tying the tap to the points.
//
//   5. 3-2-1 COUNTDOWN before the first gem, so the round doesn't start with
//      a diamond already halfway down the screen while the child is still
//      reading the Start button.
//
//   6. LAST FIVE SECONDS go red and pulse, and gems spawn faster.
//
//   7. END SUMMARY with 1-3 stars, gems caught and best combo, instead of
//      "Loading final score...".
//
//   8. (Nov 2026) MORE BOMBS (about 1 drop in 6), a 🏁 End game button
//      (keep the points, no star — onBonusComplete(score, { endedEarly })),
//      and the read-aloud starts straight away at a quicker pace
//      (kit/bonusVoice.js) instead of waiting for another tap.
//
// ===========================================================================
// TWO REAL BUGS FIXED WHILE IN HERE
// ===========================================================================
//   * UNCAUGHT GEMS WERE NEVER REMOVED FROM STATE. The old `items` array only
//     ever shrank when something was tapped. Everything a child missed stayed
//     in React state, and stayed mounted as a framer-motion node sitting at
//     y:100vh, for the whole round. On a slower classroom Chromebook that is
//     a visible frame-rate slide by about second 15. Gems now unmount on
//     animation complete.
//
//   * ONE SHARED Audio OBJECT FOR THE TAP SOUND. `clickSound.currentTime = 0`
//     on a single element means a fast tapper cancels their own sound every
//     time, so rapid play went silent. There's a small round-robin pool now.
//
// ===========================================================================
// TUNING
// ===========================================================================
// Everything a teacher would ever want to argue about is in TUNING below.
//
// NOTE ON SCORE INFLATION: the old round topped out around 150. With tiers
// and multipliers this one can pass 400. Bonus scores are written to
// leaderBoard_{slug} and to userData.game.latestBonus, so scores recorded
// before and after this change are not directly comparable. If you'd rather
// keep them in the old range, set GOLD/PURPLE to 5 and COMBO_TIERS to [] —
// the round still plays the same, it just scores flat.

import React, { useState, useEffect, useRef, useCallback } from "react";
import { playGameSound } from "../../services/gameSounds";
import confetti from "canvas-confetti";
import Faller from "./kit/Faller";
import { tapConfetti } from "./kit/tapFx";
import { sfxPool } from "./sfx";
import { bonusSay, bonusHush } from "./kit/bonusVoice";
import BonusEndEarly from "./kit/BonusEndEarly";

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

const TUNING = {
  ROUND_SECONDS: 30,
  SPAWN_MS: 520,          // normal spawn rate
  SPAWN_MS_FRENZY: 300,   // during the final countdown
  FRENZY_AT: 5,           // seconds remaining when the frenzy starts
  COMBO_TIERS: [
    { at: 10, multiplier: 3, label: "TRIPLE!" },
    { at: 5, multiplier: 2, label: "DOUBLE!" },
  ],
};

// Each gem is the same PNG, recoloured. `weight` is relative spawn frequency.
const DIAMOND_SRC =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fdiamondimagefaceblue.png?alt=media&token=b9cbc0de-cfe0-490b-b98a-94b298b1f5dd";

const GEM_TYPES = [
  {
    id: "blue",
    points: 5,
    weight: 60,
    size: 96,
    minFall: 6.5,
    maxFall: 9.5,
    filter: "none",
    glow: "rgba(90, 190, 255, 0.85)",
  },
  {
    id: "purple",
    points: 10,
    weight: 22,
    size: 88,
    minFall: 4.6,
    maxFall: 6.4,
    filter: "hue-rotate(65deg) saturate(1.35)",
    glow: "rgba(190, 120, 255, 0.9)",
  },
  {
    id: "gold",
    points: 25,
    weight: 8,
    size: 104,
    minFall: 3.2,
    maxFall: 4.4,
    filter: "hue-rotate(-155deg) saturate(2.1) brightness(1.18)",
    glow: "rgba(255, 205, 60, 0.95)",
  },
  {
    id: "bomb",
    points: -5,
    weight: 17, // was 10 — more bombs (Nov 2026)
    size: 84,
    minFall: 5.2,
    maxFall: 7.2,
    filter: null, // rendered as an emoji, not the diamond image
    glow: "rgba(255, 90, 90, 0.9)",
  },
];

const TOTAL_WEIGHT = GEM_TYPES.reduce((sum, g) => sum + g.weight, 0);

const pickGemType = () => {
  let roll = Math.random() * TOTAL_WEIGHT;
  for (const gem of GEM_TYPES) {
    roll -= gem.weight;
    if (roll <= 0) return gem;
  }
  return GEM_TYPES[0];
};

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

const TAP_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Ftapsound1.mp3?alt=media&token=c60530e0-337c-4481-ae9c-87a64c076686";
const CHIME_SOUND_URL =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/sounds%2Fbongsound1.mp3?alt=media&token=f47df164-06b3-494e-ba16-5ab54f707306";
const backgroundMusicUrl =
  "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/gamemusic%2Fbonus-round1.mp3?alt=media&token=e4c606bb-90ca-4a78-9f2e-34a709084464";


const TALK_BTN_WIDTH = 120;
const TALK_BTN_HEIGHT = 120;

const INTRO_SCRIPT =
  "Tap the falling gems to score points. Blue gems are worth five, purple are worth ten, and gold are worth twenty five. Watch out for the bombs, they take points away. Catch five in a row for double points. Click the yellow Start button when you are ready.";

// ---------------------------------------------------------------------------

const BonusRound = ({ onBonusComplete, placeholderImage }) => {
  const [bonusScore, setBonusScore] = useState(0);
  const [items, setItems] = useState([]);
  const [pops, setPops] = useState([]);
  const [timer, setTimer] = useState(TUNING.ROUND_SECONDS);
  const [isFinished, setIsFinished] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [countdown, setCountdown] = useState(null); // 3, 2, 1, "GO!", then null

  const [combo, setCombo] = useState(0);
  const [bestCombo, setBestCombo] = useState(0);
  const [gemsCaught, setGemsCaught] = useState(0);
  const [comboFlash, setComboFlash] = useState(null);
  const [screenShake, setScreenShake] = useState(false);
  const [paused, setPaused] = useState(false); // the End game question is up
  const [endedEarly, setEndedEarly] = useState(false);
  const sentRef = useRef(false);

  const bonusScoreRef = useRef(bonusScore);
  useEffect(() => {
    bonusScoreRef.current = bonusScore;
  }, [bonusScore]);

  const timerRef = useRef(timer);
  useEffect(() => {
    timerRef.current = timer;
  }, [timer]);

  const musicRef = useRef(null);
  const dropIntervalRef = useRef(null);
  const playTap = sfxPool(TAP_SOUND_URL);
  const playChime = sfxPool(CHIME_SOUND_URL, 3);

  // -------- Current combo multiplier --------
  const multiplierFor = (streak) => {
    for (const tier of TUNING.COMBO_TIERS) {
      if (streak >= tier.at) return tier;
    }
    return { multiplier: 1, label: null };
  };
  const activeTier = multiplierFor(combo);

  // =========================================================================
  // TTS — unchanged from the previous version of this file
  // =========================================================================
  const canSpeak = typeof window !== "undefined" && "speechSynthesis" in window;
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState(
    localStorage.getItem("ttsVoiceName") || ""
  );
  const [userActivated, setUserActivated] = useState(false);

  const PREFERRED_VOICE_PATTERNS = [
    /Microsoft .*? (Aria|Jenny|Guy) .*? Online .*? Natural/i,
    /Google US English/i,
    /Samantha/i, /Victoria/i, /Daniel/i,
    /Microsoft Zira/i, /Microsoft David/i, /Microsoft Mark/i,
  ];

  const chooseBestVoice = (list) => {
    for (const pat of PREFERRED_VOICE_PATTERNS) {
      const v = list.find((v) => pat.test(v.name));
      if (v) return v;
    }
    return (
      list.find((v) => /en(-|_)?US/i.test(v.lang)) ||
      list.find((v) => /^en/i.test(v.lang)) ||
      list[0] ||
      null
    );
  };

  const loadVoices = useCallback(() => {
    const list = window.speechSynthesis.getVoices() || [];
    setVoices(list);
    if (!selectedVoiceName && list.length) {
      const best = chooseBestVoice(list);
      if (best) {
        setSelectedVoiceName(best.name);
        localStorage.setItem("ttsVoiceName", best.name);
      }
    }
  }, [selectedVoiceName]);

  const waitForVoices = (maxWaitMs = 1500) =>
    new Promise((resolve) => {
      const start = Date.now();
      const check = () => {
        const list = window.speechSynthesis?.getVoices?.() || [];
        if (list.length || Date.now() - start > maxWaitMs) return resolve(list);
        setTimeout(check, 100);
      };
      check();
    });

  const unlockTTS = async () => {
    try { window.speechSynthesis?.resume?.(); } catch {}
    try {
      const u = new SpeechSynthesisUtterance(" ");
      u.volume = 0;
      window.speechSynthesis.speak(u);
      window.speechSynthesis.cancel();
    } catch {}
  };

  useEffect(() => {
    const onFirstGesture = async () => {
      setUserActivated(true);
      await unlockTTS();
      await waitForVoices();
    };
    window.addEventListener("pointerdown", onFirstGesture, { once: true, passive: true });
    return () => window.removeEventListener("pointerdown", onFirstGesture);
  }, []);

  useEffect(() => {
    if (!canSpeak) return;
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => { window.speechSynthesis.onvoiceschanged = null; };
  }, [canSpeak, loadVoices]);

  // Quick and immediate (kit/bonusVoice.js): no waiting on the voice list,
  // 1.15 speed. The cloud voice picks it up like every other utterance.
  const speakText = async (text) => {
    if (!canSpeak || !text) return;
    bonusSay(text, {
      onStart: () => {
        setIsSpeaking(true);
        if (musicRef.current) musicRef.current.volume = 0.1;
      },
      onEnd: () => {
        setIsSpeaking(false);
        if (musicRef.current && !isMuted) musicRef.current.volume = 0.3;
      },
    });
  };

  const stopSpeaking = () => {
    if (!canSpeak) return;
    bonusHush();
    setIsSpeaking(false);
    if (musicRef.current && !isMuted) musicRef.current.volume = 0.3;
  };

  // =========================================================================
  // Music
  // =========================================================================
  useEffect(() => {
    musicRef.current = new Audio(backgroundMusicUrl);
    musicRef.current.loop = true;
    musicRef.current.volume = 0.3;
    musicRef.current.play().catch(() => {});
    return () => {
      musicRef.current?.pause();
      if (musicRef.current) musicRef.current.currentTime = 0;
    };
  }, []);

  // Read the directions straight away — the child already pressed a button
  // to get here, so the browser allows it. (It used to wait for ANOTHER tap.)
  // If the browser still blocked it, the first tap on the card starts it.
  const introSpoken = useRef(false);
  useEffect(() => {
    const t = setTimeout(() => {
      if (!introSpoken.current && showIntro) {
        introSpoken.current = true;
        speakText(INTRO_SCRIPT);
      }
    }, 150);
    return () => clearTimeout(t);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (userActivated && showIntro && !isSpeaking && !introSpoken.current) {
      introSpoken.current = true;
      speakText(INTRO_SCRIPT);
    }
  }, [userActivated]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Round flow: intro -> 3,2,1,GO -> play -> summary
  // =========================================================================

  const beginCountdown = () => {
    stopSpeaking();
    setShowIntro(false);
    setCountdown(3);
  };

  useEffect(() => {
    if (countdown === null) return;

    if (countdown === "GO!") {
      const t = setTimeout(() => setCountdown(null), 550);
      return () => clearTimeout(t);
    }

    const t = setTimeout(() => {
      setCountdown((c) => (c > 1 ? c - 1 : "GO!"));
    }, 700);
    return () => clearTimeout(t);
  }, [countdown]);

  // Spawner. Restarted when the frenzy kicks in so the rate can change.
  const isFrenzy = !isFinished && timer <= TUNING.FRENZY_AT && timer > 0;

  useEffect(() => {
    if (showIntro || countdown !== null || isFinished || paused) return;

    const rate = isFrenzy ? TUNING.SPAWN_MS_FRENZY : TUNING.SPAWN_MS;

    dropIntervalRef.current = setInterval(() => {
      const gem = pickGemType();
      const id =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;

      setItems((prev) => [
        ...prev,
        {
          id,
          gem,
          // Keep gems clear of the score HUD on the left and the mute button
          // bottom-right by spawning within 6%-86% of the width.
          left: 6 + Math.random() * 80,
          speed: gem.minFall + Math.random() * (gem.maxFall - gem.minFall),
          drift: (Math.random() - 0.5) * 40,
          spin: Math.random() > 0.5 ? 360 : -360,
        },
      ]);
    }, rate);

    return () => clearInterval(dropIntervalRef.current);
  }, [showIntro, countdown, isFinished, isFrenzy, paused]);

  // Round clock
  useEffect(() => {
    if (showIntro || countdown !== null || isFinished || paused) return;

    const t = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(t);
          if (dropIntervalRef.current) {
            clearInterval(dropIntervalRef.current);
            dropIntervalRef.current = null;
          }
          setIsFinished(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(t);
  }, [showIntro, countdown, isFinished, paused]);

  // Hand the score back once the summary has had a moment on screen (or
  // straight away with Continue). An early end keeps the points but tells
  // the parent, which then doesn't count it as finishing the game (no star).
  const sendScore = useCallback(() => {
    if (sentRef.current) return;
    sentRef.current = true;
    onBonusComplete?.(Math.max(0, bonusScoreRef.current), { endedEarly });
  }, [onBonusComplete, endedEarly]);

  useEffect(() => {
    if (!isFinished) return;

    const finalScore = bonusScoreRef.current;
    if (finalScore > 0 && !endedEarly) {
      confetti({ spread: 100, particleCount: 120, startVelocity: 50, origin: { y: 0.5 } });
    }

    const t = setTimeout(sendScore, endedEarly ? 4200 : 3600);
    return () => clearTimeout(t);
  }, [isFinished, endedEarly, sendScore]);

  const endEarly = () => {
    if (dropIntervalRef.current) {
      clearInterval(dropIntervalRef.current);
      dropIntervalRef.current = null;
    }
    setEndedEarly(true);
    setIsFinished(true);
  };

  useEffect(() => {
    return () => {
      clearInterval(dropIntervalRef.current);
      stopSpeaking();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================================================================
  // Tapping
  // =========================================================================

  const removeItem = useCallback((id) => {
    setItems((prev) => prev.filter((it) => it.id !== id));
  }, []);

  // Stable handlers for the falling gems (kit/Faller.js never re-draws a gem
  // that is already falling, so it keeps the first function it was given —
  // these always reach the latest handleTap through a ref).
  const tapRef = useRef(null);
  const onGemTap = useCallback((item, e) => tapRef.current?.(item, e), []);
  const onGemGone = useCallback((item) => removeItem(item.id), [removeItem]);

  const [bursts, setBursts] = useState([]);
  const addBurst = (x, y, kind) => {
    if (!Number.isFinite(x) || !Number.isFinite(y)) return;
    const id = `${Date.now()}-${Math.random()}`;
    setBursts((prev) => [...prev.slice(-6), { id, x, y, kind }]);
    setTimeout(() => setBursts((prev) => prev.filter((b) => b.id !== id)), 420);
  };

  const addPop = (text, left, kind) => {
    const id = `${Date.now()}-${Math.random()}`;
    setPops((prev) => [...prev, { id, text, left, kind }]);
    setTimeout(() => {
      setPops((prev) => prev.filter((p) => p.id !== id));
    }, 900);
  };

  const handleTap = (item, event) => {
    if (isFinished || paused) return;

    const { gem } = item;
    removeItem(item.id);
    addBurst(event?.clientX, event?.clientY, gem.id);

    // Where on the screen the pop should appear, as a percentage.
    const leftPct = event?.clientX
      ? (event.clientX / window.innerWidth) * 100
      : item.left;

    if (gem.id === "bomb") {
      playGameSound("bonusBomb", "diamondTap", playTap);
      setCombo(0);
      setScreenShake(true);
      setTimeout(() => setScreenShake(false), 400);
      // Floored at zero. A child ending the bonus round on a negative number
      // is a bad experience and it would also poison the leaderboard.
      setBonusScore((prev) => Math.max(0, prev + gem.points));
      addPop("-5", leftPct, "bad");
      return;
    }

    const nextCombo = combo + 1;
    const tier = multiplierFor(nextCombo);
    const awarded = gem.points * tier.multiplier;

    setCombo(nextCombo);
    setBestCombo((b) => Math.max(b, nextCombo));
    setGemsCaught((g) => g + 1);
    setBonusScore((prev) => prev + awarded);
    addPop(`+${awarded}`, leftPct, gem.id);

    // Announce the moment a new tier is reached, not on every catch above it.
    const prevTier = multiplierFor(combo);
    const tierUp = Boolean(tier.label && tier.multiplier > prevTier.multiplier);
    if (tierUp) {
      setComboFlash(tier.label);
      setTimeout(() => setComboFlash(null), 1100);
    }
    // a gold gem or a new ×2 / ×3 combo = the "extra points" sound; any
    // other gem = the "right item" sound (Layout Builder → 🎵 Game sounds)
    if (tierUp || gem.id === "gold") playGameSound("bonusPower", "diamondTap", playChime);
    else playGameSound("bonusGood", "diamondTap", playTap);

    if (gem.id === "gold") {
      tapConfetti({
        spread: 70,
        scalar: 0.9,
        startVelocity: 40,
        particleCount: 45,
        colors: ["#ffd43b", "#fff3bf", "#f59f00"],
        origin: { x: leftPct / 100, y: 0.4 },
      });
    } else if (gem.id === "purple") {
      tapConfetti({ spread: 55, scalar: 0.7, startVelocity: 32, particleCount: 22, origin: { x: leftPct / 100, y: 0.45 } });
    }
  };
  tapRef.current = handleTap;

  // =========================================================================
  // Summary
  // =========================================================================

  const stars = endedEarly ? 0 : bonusScore >= 200 ? 3 : bonusScore >= 100 ? 2 : bonusScore > 0 ? 1 : 0;
  const timePct = (timer / TUNING.ROUND_SECONDS) * 100;

  const bgImage =
    placeholderImage ||
    "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fpirate-ship-background.png?alt=media&token=3912623d-30d2-468a-ab2e-449398d2bce2";

  return (
    <>
      <style>{`
        .bonus-container {
          position: fixed; inset: 0; overflow: hidden;
          background-image: url('${bgImage}');
          background-size: cover; background-position: center; background-repeat: no-repeat;
          background-color: #0e2440;
          font-family: "Baloo 2", "Fredoka", "Comic Sans MS", "Trebuchet MS", sans-serif;
          user-select: none; -webkit-user-select: none; touch-action: manipulation;
        }
        .bonus-container::before {
          content: ""; position: absolute; inset: 0; pointer-events: none;
          background: radial-gradient(120% 85% at 50% 0%,
            rgba(8,23,41,0.10) 0%, rgba(8,23,41,0.42) 60%, rgba(8,23,41,0.72) 100%);
        }
        .bonus-container.shake { animation: bonus-shake 380ms ease; }
        @keyframes bonus-shake {
          0%,100% { transform: translate(0,0); }
          20% { transform: translate(-9px, 4px); }
          40% { transform: translate(8px, -5px); }
          60% { transform: translate(-6px, 3px); }
          80% { transform: translate(5px, -2px); }
        }

        .bonus-logo {
          position: absolute; top: 12px; left: 50%; transform: translateX(-50%);
          max-width: 120px; width: 14vw; min-width: 74px; height: auto; z-index: 4;
          filter: drop-shadow(0 3px 10px rgba(0,0,0,.5));
        }

        /* ---------- HUD ---------- */
        .bonus-hud {
          position: absolute; top: 14px; left: 14px; right: 14px; z-index: 5;
          display: flex; justify-content: space-between; align-items: flex-start;
          gap: 12px; pointer-events: none;
        }
        .hud-card {
          background: rgba(8,23,41,.86); border: 3px solid rgba(255,255,255,.28);
          border-radius: 18px; padding: 10px 16px; color: #fff;
          box-shadow: 0 5px 0 rgba(0,0,0,.35); min-width: 132px;
        }
        .hud-label {
          display: block; font-size: 12px; letter-spacing: .12em; text-transform: uppercase;
          font-weight: 800; color: #ffd873; margin-bottom: 2px;
        }
        .hud-value { font-size: 34px; font-weight: 900; line-height: 1; }
        .hud-value.urgent { color: #ff6b6b; animation: hud-pulse .5s ease-in-out infinite; }
        @keyframes hud-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.16); } }

        .time-track {
          margin-top: 7px; height: 10px; width: 100%; border-radius: 999px;
          background: rgba(0,0,0,.5); overflow: hidden;
        }
        .time-fill {
          height: 100%; border-radius: 999px; transition: width 1s linear;
          background: linear-gradient(90deg, #4fe39a, #ffd43b);
        }
        .time-fill.urgent { background: linear-gradient(90deg, #ff8a5c, #ff3b3b); }

        /* ---------- Combo meter ---------- */
        .combo-wrap {
          position: absolute; left: 50%; transform: translateX(-50%);
          top: 108px; z-index: 5; width: min(340px, 62vw); pointer-events: none;
          text-align: center;
        }
        .combo-track {
          height: 14px; border-radius: 999px; background: rgba(8,23,41,.8);
          border: 3px solid rgba(255,255,255,.3); overflow: hidden;
        }
        .combo-fill {
          height: 100%; border-radius: 999px; transition: width 220ms ease;
          background: linear-gradient(90deg, #ff9f43, #ff5e3a);
        }
        .combo-text {
          margin-top: 5px; font-size: 16px; font-weight: 900; color: #fff;
          text-shadow: 0 2px 5px rgba(0,0,0,.9);
        }
        .combo-mult {
          display: inline-block; margin-left: 7px; padding: 2px 10px; border-radius: 999px;
          background: linear-gradient(180deg,#ffd43b,#f59f00); color: #3d2b00;
        }

        .combo-flash {
          position: absolute; left: 50%; top: 36%; transform: translateX(-50%);
          z-index: 8; font-size: clamp(40px, 8vw, 92px); font-weight: 900; color: #fff;
          -webkit-text-stroke: 3px #b5480b; text-shadow: 0 0 40px rgba(255,180,60,.95);
          pointer-events: none; animation: combo-flash 1.1s ease forwards;
        }
        @keyframes combo-flash {
          0%   { opacity: 0; transform: translateX(-50%) scale(.4) rotate(-9deg); }
          25%  { opacity: 1; transform: translateX(-50%) scale(1.2) rotate(3deg); }
          40%  { transform: translateX(-50%) scale(1) rotate(0); }
          75%  { opacity: 1; }
          100% { opacity: 0; transform: translateX(-50%) scale(1.1) translateY(-40px); }
        }

        /* ---------- Score pops ---------- */
        .pop {
          position: absolute; top: 44%; z-index: 7; pointer-events: none;
          font-size: clamp(28px, 4.4vw, 52px); font-weight: 900; color: #fff;
          -webkit-text-stroke: 2px rgba(0,0,0,.55);
          animation: pop-rise 900ms cubic-bezier(.25,.9,.35,1) forwards;
          transform: translateX(-50%);
        }
        .pop.blue   { color: #8ad6ff; }
        .pop.purple { color: #d5a8ff; }
        .pop.gold   { color: #ffd43b; text-shadow: 0 0 26px rgba(255,212,59,.9); }
        .pop.bad    { color: #ff6b6b; }
        @keyframes pop-rise {
          0%   { opacity: 0; transform: translateX(-50%) translateY(20px) scale(.5); }
          20%  { opacity: 1; transform: translateX(-50%) translateY(0) scale(1.25); }
          35%  { transform: translateX(-50%) translateY(-8px) scale(1); }
          100% { opacity: 0; transform: translateX(-50%) translateY(-150px) scale(.9); }
        }

        /* ---------- Falling gems ---------- */
        .gem { position: absolute; cursor: pointer; pointer-events: auto; z-index: 3;
               will-change: transform; backface-visibility: hidden; contain: layout style; touch-action: manipulation; }
        .gem img { width: 100%; height: 100%; object-fit: contain; display: block; pointer-events: none; }
        .tap-burst { position: fixed; width: 90px; height: 90px; margin: -45px 0 0 -45px; border-radius: 50%;
                     border: 5px solid rgba(140, 215, 255, .95); pointer-events: none; z-index: 6;
                     animation: tap-burst 400ms ease-out forwards; }
        .tap-burst.purple { border-color: rgba(205, 150, 255, .95); }
        .tap-burst.gold { border-color: rgba(255, 214, 80, 1); }
        .tap-burst.bomb { border-color: rgba(255, 90, 70, .95); }
        @keyframes tap-burst { from { transform: scale(.35); opacity: 1; } to { transform: scale(1.35); opacity: 0; } }
        .gem-bomb {
          display: grid; place-items: center; font-size: 62px; line-height: 1;
        }

        /* ---------- Countdown ---------- */
        .countdown {
          position: fixed; inset: 0; z-index: 9; display: grid; place-items: center;
          background: rgba(8,23,41,.55); pointer-events: none;
        }
        .countdown span {
          font-size: clamp(90px, 22vw, 240px); font-weight: 900; color: #ffd43b;
          -webkit-text-stroke: 5px #7a4b00; text-shadow: 0 0 70px rgba(255,212,59,.9);
          animation: count-pop 700ms cubic-bezier(.34,1.5,.64,1);
        }
        @keyframes count-pop {
          0%   { opacity: 0; transform: scale(2.4); }
          35%  { opacity: 1; transform: scale(.9); }
          55%  { transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }

        /* ---------- Intro ---------- */
        .intro-overlay {
          position: fixed; inset: 0; z-index: 10; display: flex;
          align-items: center; justify-content: center; padding: 16px;
          background: rgba(8,23,41,.82); backdrop-filter: blur(4px);
        }
        .intro-card {
          max-width: 640px; width: 100%; max-height: 92vh; overflow-y: auto;
          background: linear-gradient(180deg,#1b3b66,#0e2440);
          border: 5px solid #ffd43b; border-radius: 28px;
          box-shadow: 0 14px 0 rgba(0,0,0,.4), 0 30px 70px rgba(0,0,0,.6);
          padding: 26px 24px; text-align: center; color: #fff;
        }
        .intro-title {
          font-size: clamp(30px, 5vw, 46px); font-weight: 900; color: #ffd43b;
          margin-bottom: 6px; -webkit-text-stroke: 1.5px #7a4b00;
        }
        .intro-sub { font-size: 17px; opacity: .9; margin-bottom: 16px; }
        .gem-key { display: grid; gap: 8px; margin: 0 0 18px; }
        .gem-key-row {
          display: flex; align-items: center; gap: 12px; text-align: left;
          background: rgba(0,0,0,.3); border-radius: 14px; padding: 8px 14px;
          font-size: 17px; font-weight: 700;
        }
        .gem-key-row .swatch { width: 42px; height: 42px; flex: 0 0 42px; display: grid; place-items: center; }
        .gem-key-row .swatch img { width: 100%; height: 100%; object-fit: contain; }
        .gem-key-row .pts { margin-left: auto; font-weight: 900; color: #ffd43b; font-size: 19px; }
        .gem-key-row .pts.neg { color: #ff8080; }
        .intro-actions { display: flex; gap: 14px; align-items: center; justify-content: center; flex-wrap: wrap; }
        .start-btn {
          font-family: inherit; padding: 16px 42px; border-radius: 18px; border: none;
          cursor: pointer; font-size: 26px; font-weight: 900; color: #3d2b00;
          background: linear-gradient(180deg,#ffe066,#ffc93c);
          box-shadow: 0 7px 0 #c99a00; transition: transform 120ms ease;
        }
        .start-btn:active { transform: translateY(6px); box-shadow: 0 1px 0 #c99a00; }
        .stop-btn {
          font-family: inherit; padding: 12px 20px; border-radius: 14px; border: none;
          cursor: pointer; font-size: 17px; font-weight: 800; color: #fff;
          background: #ff5a5a; box-shadow: 0 5px 0 #c02b2b;
        }
        .talk-img-btn { background: none; border: none; padding: 0; cursor: pointer; line-height: 0; }

        /* ---------- Summary ---------- */
        .summary-overlay {
          position: fixed; inset: 0; z-index: 11; display: grid; place-items: center;
          padding: 16px; background: rgba(8,23,41,.86); backdrop-filter: blur(5px);
          animation: fade-in 320ms ease;
        }
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .summary-card {
          max-width: 560px; width: 100%; text-align: center; color: #fff;
          background: linear-gradient(180deg,#1b3b66,#0e2440);
          border: 5px solid #ffd43b; border-radius: 30px; padding: 30px 26px;
          box-shadow: 0 14px 0 rgba(0,0,0,.4), 0 30px 70px rgba(0,0,0,.6);
          animation: summary-in 480ms cubic-bezier(.34,1.4,.64,1);
        }
        @keyframes summary-in {
          from { opacity: 0; transform: translateY(40px) scale(.9); }
          to   { opacity: 1; transform: none; }
        }
        .summary-title { font-size: clamp(28px,5vw,44px); font-weight: 900; color: #ffd43b; margin-bottom: 10px; }
        .stars { font-size: clamp(38px,7vw,64px); letter-spacing: 6px; margin-bottom: 8px; }
        .stars .star { display: inline-block; animation: star-in 500ms cubic-bezier(.34,1.6,.64,1) backwards; }
        .stars .star:nth-child(1) { animation-delay: 320ms; }
        .stars .star:nth-child(2) { animation-delay: 520ms; }
        .stars .star:nth-child(3) { animation-delay: 720ms; }
        .stars .dim { opacity: .22; }
        @keyframes star-in { from { opacity: 0; transform: scale(2.6) rotate(-40deg); } to { opacity: 1; transform: none; } }
        .summary-score { font-size: clamp(52px,11vw,92px); font-weight: 900; line-height: 1; color: #fff; }
        .summary-score-label { font-size: 14px; letter-spacing: .16em; text-transform: uppercase; color: #ffd873; font-weight: 800; }
        .summary-stats { display: flex; justify-content: center; gap: 26px; margin-top: 18px; flex-wrap: wrap; }
        .summary-stat .n { font-size: 30px; font-weight: 900; color: #ffd43b; }
        .summary-stat .k { font-size: 13px; text-transform: uppercase; letter-spacing: .1em; opacity: .85; }

        /* ---------- Chrome ---------- */
        .mute-button {
          position: fixed; bottom: 16px; right: 16px; background: none; border: none;
          cursor: pointer; z-index: 6; padding: 0;
        }
        .mute-button img { width: 78px; height: 90px; filter: drop-shadow(0 5px 12px rgba(0,0,0,.55)); }
        .bonus-footer {
          position: absolute; bottom: 0; left: 0; right: 0; z-index: 2;
          font-size: 12px; color: rgba(255,255,255,.6); text-align: center; padding: 8px 0;
        }

        .early-note { margin: 12px auto 0; max-width: 420px; padding: 8px 14px; border-radius: 14px; background: rgba(255,255,255,.1); font-size: 16px; font-weight: 700; color: #ffe8a3; }
        .summary-continue { margin-top: 16px; font-family: inherit; padding: 12px 30px; border-radius: 16px; border: none; cursor: pointer; font-size: 20px; font-weight: 900; color: #fff; background: linear-gradient(180deg,#4fd77a,#22b455); box-shadow: 0 5px 0 #178a40; }

        @media (max-width: 560px) {
          .hud-card { min-width: 104px; padding: 8px 12px; }
          .hud-value { font-size: 26px; }
          .bonus-logo { display: none; }
          .combo-wrap { top: 92px; }
          .mute-button img { width: 58px; height: 66px; }
        }

        @media (prefers-reduced-motion: reduce) {
          .bonus-container.shake,
          .hud-value.urgent,
          .combo-flash,
          .countdown span,
          .stars .star { animation: none !important; }
        }
      `}</style>

      {/* ---------------- Intro ---------------- */}
      {showIntro && (
        <div className="intro-overlay">
          <div className="intro-card">
            <div className="intro-title">💎 Bonus Round!</div>
            <div className="intro-sub">Tap the falling gems before they hit the water.</div>

            <div className="gem-key">
              {GEM_TYPES.map((gem) => (
                <div className="gem-key-row" key={gem.id}>
                  <span className="swatch">
                    {gem.id === "bomb" ? (
                      <span style={{ fontSize: 30 }}>💣</span>
                    ) : (
                      <img src={DIAMOND_SRC} alt="" style={{ filter: gem.filter }} />
                    )}
                  </span>
                  <span>
                    {gem.id === "bomb"
                      ? "Bomb — don't tap it!"
                      : `${gem.id[0].toUpperCase() + gem.id.slice(1)} gem`}
                  </span>
                  <span className={`pts ${gem.points < 0 ? "neg" : ""}`}>
                    {gem.points > 0 ? `+${gem.points}` : gem.points}
                  </span>
                </div>
              ))}
              <div className="gem-key-row">
                <span className="swatch" style={{ fontSize: 26 }}>🔥</span>
                <span>5 in a row = double points, 10 = triple!</span>
              </div>
            </div>

            <div className="intro-actions">
              <button
                className="talk-img-btn"
                onClick={async () => {
                  await unlockTTS();
                  await speakText(INTRO_SCRIPT);
                }}
                disabled={!canSpeak}
                title={canSpeak ? "Read the directions" : "Speech not supported in this browser"}
                aria-label="Read the directions out loud"
              >
                <img
                  src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Ftalktextbutton.png?alt=media&token=c3a19304-b98a-43cf-9d81-c9b80000375e"
                  alt="Talk text"
                  style={{
                    width: TALK_BTN_WIDTH,
                    height: TALK_BTN_HEIGHT,
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </button>

              {isSpeaking && (
                <button className="stop-btn" onClick={stopSpeaking}>⏹ Stop</button>
              )}

              <button className="start-btn" onClick={beginCountdown}>Start!</button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Countdown ---------------- */}
      {countdown !== null && (
        <div className="countdown">
          <span key={String(countdown)}>{countdown}</span>
        </div>
      )}

      {/* ---------------- Game ---------------- */}
      <div className={`bonus-container ${screenShake ? "shake" : ""}`}>
        <img
          src="https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2FSBLLogoBlackTM.png?alt=media&token=2f8fde38-b373-4367-9ade-a7b68bae3b6f"
          alt="SBL Logo"
          className="bonus-logo"
        />

        <div className="bonus-hud">
          <div className="hud-card">
            <span className="hud-label">Score</span>
            <div className="hud-value">{bonusScore}</div>
          </div>

          <div className="hud-card" style={{ textAlign: "right" }}>
            <span className="hud-label">Time</span>
            <div className={`hud-value ${isFrenzy ? "urgent" : ""}`}>{timer}s</div>
            <div className="time-track">
              <div
                className={`time-fill ${isFrenzy ? "urgent" : ""}`}
                style={{ width: `${timePct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Combo meter — only once there's something to show */}
        {!showIntro && !isFinished && combo > 0 && (
          <div className="combo-wrap">
            <div className="combo-track">
              <div
                className="combo-fill"
                style={{ width: `${Math.min(100, (combo / 10) * 100)}%` }}
              />
            </div>
            <div className="combo-text">
              🔥 {combo} in a row
              {activeTier.multiplier > 1 && (
                <span className="combo-mult">×{activeTier.multiplier}</span>
              )}
            </div>
          </div>
        )}

        {comboFlash && <div className="combo-flash">{comboFlash}</div>}

        {/* Falling gems — moved by the browser's compositor (kit/Faller.js),
            so a burst of taps never freezes the ones still falling */}
        {!isFinished &&
          !showIntro &&
          countdown === null &&
          items.map((item) => (
            <Faller
              key={item.id}
              item={item}
              className="gem"
              style={{
                left: `${item.left}%`,
                width: item.gem.size,
                height: item.gem.size,
                top: -item.gem.size,
                // A soft painted glow instead of a blur filter — a blurred shadow on
                // every falling gem was re-drawn each frame and made the round stutter.
                background: `radial-gradient(circle, ${item.gem.glow} 0%, transparent 68%)`,
                borderRadius: "50%",
              }}
              speed={item.speed}
              drift={item.drift}
              spin={item.spin}
              distance="110vh"
              paused={paused}
              // Missed gems unmount instead of piling up in state forever.
              onDone={onGemGone}
              onTap={onGemTap}
            >
              {item.gem.id === "bomb" ? (
                <span className="gem-bomb" style={{ width: "100%", height: "100%" }}>
                  💣
                </span>
              ) : (
                <img
                  src={DIAMOND_SRC}
                  alt=""
                  draggable="false"
                  style={{ filter: item.gem.filter }}
                />
              )}
            </Faller>
          ))}

        {/* where a gem was tapped: a quick ring (CSS only) */}
        {bursts.map((b) => (
          <span key={b.id} className={`tap-burst ${b.kind}`} style={{ left: b.x, top: b.y }} />
        ))}

        {/* Floating score pops */}
        {pops.map((p) => (
          <div key={p.id} className={`pop ${p.kind}`} style={{ left: `${p.left}%` }}>
            {p.text}
          </div>
        ))}

        <button
          className="mute-button"
          onClick={() => {
            const next = !isMuted;
            setIsMuted(next);
            if (musicRef.current) musicRef.current.muted = next;
          }}
          aria-label={isMuted ? "Turn music on" : "Turn music off"}
        >
          <img
            src={
              isMuted
                ? "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Funmute-button.png?alt=media&token=e6afbbd2-41e5-41c1-b911-513fa41a4360"
                : "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/images%2Fmute-button.png?alt=media&token=9b91203e-011f-4324-bbea-300d0d273fcf"
            }
            alt=""
          />
        </button>

        <div className="bonus-footer">© Smart Board Learning LLC. All rights reserved.</div>
      </div>

      {!showIntro && !isFinished && <BonusEndEarly onEnd={endEarly} onPause={setPaused} />}

      {/* ---------------- Summary ---------------- */}
      {isFinished && (
        <div className="summary-overlay">
          <div className="summary-card">
            <div className="summary-title">{endedEarly ? "Round ended early" : "Bonus Round Complete!"}</div>

            {!endedEarly && (
              <div className="stars" aria-label={`${stars} out of 3 stars`}>
                {[0, 1, 2].map((i) => (
                  <span key={i} className={`star ${i < stars ? "" : "dim"}`}>
                    ⭐
                  </span>
                ))}
              </div>
            )}

            <div className="summary-score-label">Bonus Points</div>
            <div className="summary-score">{bonusScore}</div>

            <div className="summary-stats">
              <div className="summary-stat">
                <div className="n">{gemsCaught}</div>
                <div className="k">Gems</div>
              </div>
              <div className="summary-stat">
                <div className="n">{bestCombo}</div>
                <div className="k">Best Streak</div>
              </div>
            </div>
            {endedEarly && <p className="early-note">Your points still count! Play the whole bonus round next time to earn the ⭐ star.</p>}
            <button type="button" className="summary-continue" onClick={sendScore}>
              Continue ▶
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BonusRound;
